import { useEffect } from "react"

import { useSearchParams } from "react-router-dom"
import {
  DashboardStudentAttentionView,
  DashboardStudentSortKey,
} from "ts/types/AdminDashboard"

import { formatClassIds, parseClassIds } from "./dashboardFormat"
import useDashboardTableState from "./useDashboardTableState"

const STUDENT_SORT_KEYS: DashboardStudentSortKey[] = [
  "first_name",
  "last_name",
  "positivity",
  "total_amount",
  "bonus_amount",
  "fine_amount",
  "attendance_count",
  "salary_amount",
  "expense_amount",
]

// The shortlist's rankings, which have no column of their own.
const STUDENT_EXTRA_SORT_KEYS: DashboardStudentSortKey[] = [
  "fine_count",
  "bonus_count",
]

// Each shortlist's ranking in the table; `desc` is the default, so left out.
const APPLY_SORT: Record<
  DashboardStudentAttentionView,
  { st_sort: DashboardStudentSortKey; st_dir: "asc" | null }
> = {
  fewest_bonuses_earned: { st_sort: "bonus_count", st_dir: "asc" },
  lowest_positivity: { st_sort: "positivity", st_dir: "asc" },
  most_fined: { st_sort: "fine_count", st_dir: null },
  lowest_attendance: { st_sort: "attendance_count", st_dir: "asc" },
}

// The shared table state plus the class filter, `st_classes=12,34`.
const useStudentsTableState = () => {
  const [searchParams] = useSearchParams()
  const state = useDashboardTableState({
    prefix: "st",
    sortKeys: STUDENT_SORT_KEYS,
    extraSortKeys: STUDENT_EXTRA_SORT_KEYS,
  })
  const rawClassIds = searchParams.get("st_classes")
  const classIds = parseClassIds(rawClassIds)

  // A hand-edited link is rewritten to the ids it filters by; the filter itself
  // is the same, so the page stays.
  const normalizedClassIds = formatClassIds(classIds)
  useEffect(() => {
    if (rawClassIds !== null && rawClassIds !== normalizedClassIds)
      state.write({ st_classes: normalizedClassIds })
  }, [rawClassIds])

  const setClassIds = (ids: number[]) =>
    state.write({ st_classes: formatClassIds(ids), st_page: null })

  const clearFilters = () =>
    state.write({ st_search: null, st_classes: null, st_page: null })

  // The whole school in the shortlist's order, from the first page.
  const applyView = (view: DashboardStudentAttentionView) =>
    state.write({
      ...APPLY_SORT[view],
      st_search: null,
      st_classes: null,
      st_page: null,
    })

  const clearSort = () =>
    state.write({ st_sort: null, st_dir: null, st_page: null })

  return {
    ...state,
    classIds,
    setClassIds,
    clearFilters,
    applyView,
    clearSort,
  }
}

export default useStudentsTableState
